import React, { useMemo, useState } from "react";
import { type AccessibilityRole, type ViewStyle, Platform, StyleSheet, View } from "react-native";

import { type BoxStyle, Box } from "../Box";
import { Pressable } from "../Pressable";
import { useTheme } from "../ThemeProvider";
import { useFocusRing } from "../util/useFocusRing";
import { CardSection } from "./CardSection";

export interface CardProps {
  /**
   * Use in a controlled fashion to apply interactive selectable styles.
   * When defined, the card becomes pressable with hover/press states.
   */
  readonly selected?: boolean;

  /**
   * Callback when the card is pressed. Only applicable when `selected` is defined.
   */
  readonly onPress?: () => void;

  /**
   * Children to render inside the card.
   * If children are not CardSection components, they will be automatically wrapped in a CardSection.
   */
  readonly children?: React.ReactNode;

  /**
   * Used to provide a full-bleed by removing the default padding.
   */
  readonly full?: boolean;

  /**
   * Style prop using MADS convention for custom styling.
   */
  readonly sx?: BoxStyle;

  /**
   * Accessibility label for the card.
   */
  readonly accessibilityLabel?: string;

  /**
   * Accessibility role for the card.
   */
  readonly accessibilityRole?: AccessibilityRole | undefined;

  /**
   * Test ID for testing purposes.
   */
  readonly testID?: string;

  /**
   * When true, the card is not pressable and `accessibilityState.disabled` is set.
   * Only applies when `selected` is defined (interactive mode).
   */
  readonly disabled?: boolean;
}

function flattenChildren(children: React.ReactNode): React.ReactNode[] {
  return React.Children.toArray(children).flatMap((child) => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      return flattenChildren((child.props as { children?: React.ReactNode }).children);
    }
    return [child];
  });
}

function hasCardSectionChild(children: React.ReactNode): boolean {
  return flattenChildren(children).some(
    (child) => React.isValidElement(child) && child.type === CardSection,
  );
}

export function Card({
  selected,
  onPress,
  children,
  full = false,
  sx,
  accessibilityLabel,
  accessibilityRole = "link",
  testID,
  disabled = false,
}: CardProps): React.JSX.Element {
  const theme = useTheme();
  const { isFocusVisible, focusProps } = useFocusRing();
  const [isHovered, setIsHovered] = useState(false);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: theme.colors.background,
          borderWidth: 1,
          borderColor: theme.colors.borderWeak,
          borderRadius: theme.borderRadius.lg,
        },
        cardInner: {
          overflow: "hidden",
          borderRadius: theme.borderRadius.lg,
        },
        contentWrapper: {
          flexDirection: "column",
          paddingVertical: full ? 0 : theme.spacing.s4,
          gap: theme.spacing.s2,
        },
        selected: {
          borderColor: theme.colors.borderPrimary,
          borderWidth: 2,
        },
        selectedShadow: {
          boxShadow: isHovered ? theme.shadows.low : theme.shadows.medium,
        },
        unselectedShadow: {
          boxShadow: isHovered ? theme.shadows.low : "",
        },
        activeShadow: {
          boxShadow: theme.shadows.inset,
        },
      }),
    [theme, full, isHovered],
  );

  const isInteractive = selected !== undefined;
  const wrapChildren = !children || !hasCardSectionChild(children);
  const content = wrapChildren ? <CardSection>{children}</CardSection> : children;
  const wrappedContent = <View style={styles.contentWrapper}>{content}</View>;

  const cardStyle: BoxStyle = {
    ...styles.card,
    ...(selected === true ? styles.selected : {}),
    ...(typeof sx === "object" && sx !== null ? sx : {}),
  };

  if (isInteractive) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        onFocus={focusProps.onFocus}
        onBlur={focusProps.onBlur}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        accessibilityState={{ selected: !!selected, disabled }}
        testID={testID}
        sx={({ pressed }: { pressed: boolean }) => ({
          ...cardStyle,
          ...(selected && !pressed ? styles.selectedShadow : {}),
          ...(!selected && !pressed ? styles.unselectedShadow : {}),
          ...(Platform.OS === "web" && isFocusVisible
            ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
              ({
                outlineStyle: "solid",
                outlineWidth: 2,
                outlineColor: theme.colors.utilityFocus,
                outlineOffset: 2,
              } as ViewStyle)
            : {}),
        })}
      >
        {({ pressed }) => (
          <View style={[styles.cardInner, pressed ? styles.activeShadow : {}]}>
            {wrappedContent}
          </View>
        )}
      </Pressable>
    );
  } else {
    return (
      <Box accessibilityLabel={accessibilityLabel} testID={testID} sx={cardStyle}>
        {wrappedContent}
      </Box>
    );
  }
}
